import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { SUPPORT_EMAIL, ROUTES } from '@shared/constants';
import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import TextButton from '@components/TextButton';
import Button from '@components/Button';
import CustomTextInput from '@components/CustomTextInput';
import ErrorMessage, { ERROR_MESSAGE_TYPES } from '@components/ErrorMessage';
import {
  ScreenContainer,
  WithHeaderComponentContainer,
  HeaderContainer,
  MainContainer,
  TextContainer,
  LinkContainer,
  StyledSignUpLink,
} from './styles';
import { verifyEmailWithCode, resendVerificationEmail } from '@services/cognitoUserService';
import { normalizeCognitoVerificationErrorMessage } from '@services/cognitoEmailVerificationService';
import { isCognitoUserAlreadyConfirmedError } from '@services/cognitoErrorNormalizer';
import {
  openSupportEmailPopupToSignIn,
} from '../shared/supportPopupHelpers';
import { getCodeSentMessage, normalizeCodeDeliveryDetails } from '../shared/codeDeliveryDetails';

const RESEND_COOLDOWN_MS = 60000;

export const VerifyEmailSent = ({ history }) => {
  const routeState = history?.location?.state || {};
  const {
    email,
    autoResent,
    sendError,
    codeDeliveryDetails: codeDeliveryDetailsFromState,
    afterVerifiedRedirectTo,
    afterVerifiedRedirectState,
  } = routeState;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [codeDeliveryDetails, setCodeDeliveryDetails] = useState(
    normalizeCodeDeliveryDetails(codeDeliveryDetailsFromState),
  );

  const [codeWasSent, setCodeWasSent] = useState(Boolean(autoResent) || Boolean(codeDeliveryDetailsFromState));

  const resendButtonDisabled = submitting || !email || resendDisabled;

  const deliveryMessage = useMemo(() => {
    if (!codeWasSent) return null;
    return getCodeSentMessage({ t, codeDeliveryDetails });
  }, [codeWasSent, t, codeDeliveryDetails]);

  const openAlreadyConfirmedHelpPopup = useCallback(() => {
    openSupportEmailPopupToSignIn({
      t,
      titleI18nKey: 'Errors.verificationCodeUnavailableTitle',
      bodyI18nKey: 'Errors.verificationCodeUnavailableBody',
      email: SUPPORT_EMAIL,
      history,
      dispatch,
    });
  }, [dispatch, history, t]);

  // If we were redirected here after an automatic resend attempt that failed,
  // surface the error inline unless the account is already confirmed.
  useEffect(() => {
    if (!sendError) return;
    if (isCognitoUserAlreadyConfirmedError(sendError)) {
      openAlreadyConfirmedHelpPopup();
      return;
    }
    const msg = normalizeCognitoVerificationErrorMessage(
      sendError,
      { t, fallback: t('Errors.verificationCodeSendFailed') },
    );
    setError(msg);
  }, [sendError, openAlreadyConfirmedHelpPopup, t]);

  // Handles verification code submission
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);
      setNotification(null);

      let postVerifyRedirect = null;
      if (!email) {
        setError(t('Errors.noEmail'));
        setSubmitting(false);
        return;
      }
      try {
        const result = await verifyEmailWithCode(email, code);
        if (result.success) {
          if (afterVerifiedRedirectTo) {
            postVerifyRedirect = {
              to: afterVerifiedRedirectTo,
              state: afterVerifiedRedirectState || { email },
            };
          } else {
            setSuccess(true);
            setError(null);
            setNotification(null);
          }
        } else {
          console.log('Verification error:', result.error?.message || result.error);
          if (isCognitoUserAlreadyConfirmedError(result.error)) {
            openAlreadyConfirmedHelpPopup();
            return;
          }
          setError(
            normalizeCognitoVerificationErrorMessage(
              result.error,
              { t, fallback: t('Errors.verificationFailedTryAgain') },
            ),
          );
        }
      } catch (err) {
        if (isCognitoUserAlreadyConfirmedError(err)) {
          openAlreadyConfirmedHelpPopup();
          return;
        }
        setError(
          normalizeCognitoVerificationErrorMessage(
            err,
            { t, fallback: t('Errors.verificationFailedTryAgain') },
          ),
        );
      } finally {
        setSubmitting(false);
      }

      if (postVerifyRedirect) {
        history.push(postVerifyRedirect.to, postVerifyRedirect.state);
      }
    },
    [email, code, t, afterVerifiedRedirectTo, afterVerifiedRedirectState, history, openAlreadyConfirmedHelpPopup],
  );

  // Handles resending the verification email
  const handleResendEmail = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    setNotification(null);
    if (!email) {
      setError(t('Errors.noEmail'));
      setSubmitting(false);
      return;
    }
    setResendDisabled(true);
    setTimeout(() => setResendDisabled(false), RESEND_COOLDOWN_MS); // Re-enable after 60 seconds
    try {
      const result = await resendVerificationEmail(email);
      if (result.success) {
        const details = normalizeCodeDeliveryDetails(result?.result?.CodeDeliveryDetails);
        if (details) setCodeDeliveryDetails(details);
        setCodeWasSent(true);
        setNotification(getCodeSentMessage({ t, codeDeliveryDetails: details }));
        setCode(''); // Optionally clear code after resend
      } else {
        if (isCognitoUserAlreadyConfirmedError(result.error)) {
          openAlreadyConfirmedHelpPopup();
          return;
        }
        const msg = normalizeCognitoVerificationErrorMessage(
          result.error,
          { t, fallback: t('Errors.verificationCodeResendFailed') },
        );
        setError(msg);
        return;
      }
    } catch (err) {
      if (isCognitoUserAlreadyConfirmedError(err)) {
        openAlreadyConfirmedHelpPopup();
        return;
      }
      const msg = normalizeCognitoVerificationErrorMessage(
        err,
        { t, fallback: t('Errors.verificationCodeResendFailed') },
      );
      setError(msg);
      return;
    } finally {
      setSubmitting(false);
    }
  }, [email, t, openAlreadyConfirmedHelpPopup]);

  return (
    <ScreenContainer>
      <WithHeaderComponentContainer>
        <HeaderContainer>
          <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H1}>
            {t('Authentication.Email.checkEmail')}
          </Text>
        </HeaderContainer>
        <MainContainer>
        {!success && deliveryMessage && (
            <TextContainer>
              <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H4}>
                {deliveryMessage}
              </Text>
            </TextContainer>
          )}
          {!success ? (
            <form style={{ marginTop: 24 }} onSubmit={handleSubmit}>
              <CustomTextInput
                id="verification_code"
                label={t('Authentication.Distribution.verificationCode')}
                name="verificationCode"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                type="text"
                autoFocus
              />
              {error && <ErrorMessage type={ERROR_MESSAGE_TYPES.CUSTOM} customText={error}></ErrorMessage>}
              {notification && (
                <div style={{ color: '#2e7d32', marginTop: 8, textAlign: 'center' }}>{notification}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Button
                  type="submit"
                  disabled={submitting || !code}
                >
                  {submitting ? t('Actions.Auth.verifying', 'Verifying...') : t('Authentication.Distribution.verify')}
                </Button>
              </div>
            </form>
          ) : (
            <Text textColor={TEXT_COLOR.DARK_BLUE} size={TEXT_SIZE.H4} style={{ marginTop: 24 }}>
              {t('Authentication.Email.verifiedSuccess')}
            </Text>
          )}
        </MainContainer>
        <LinkContainer style={{ display: 'flex', justifyContent: 'center', gap: 32, marginTop: 24 }}>
          {success && (
            <StyledSignUpLink id="sign_in_link" to={ROUTES.SIGN_IN}>
              <TextButton>
                <Text size={TEXT_SIZE.H4}>{t('Actions.Auth.signIn')}</Text>
              </TextButton>
            </StyledSignUpLink>
          )}
          {!success && (
            <TextButton id="resend_email_link_bottom" onClick={handleResendEmail} disabled={resendButtonDisabled} style={{ opacity: resendButtonDisabled ? 0.5 : 1 }}>
              <Text size={TEXT_SIZE.H4}>{t('Authentication.Email.resend')}</Text>
            </TextButton>
          )}
        </LinkContainer>
      </WithHeaderComponentContainer>
    </ScreenContainer>
  );
};
